/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { api } from "../lib/api";
import { IUserRegistrationData } from "@/interfaces/User.interface";
import { IProject } from "@/interfaces/Project.interface";
import { ICreateProjectData } from "./../../interfaces/Project.interface";
import { IApiExtendedParams } from "@/interfaces/Common.interface";
import { IAnalytics, IAnalyticsAPIParams } from "@/interfaces/Analytics.interface";

interface ICreateProjectMutationProps extends ICreateProjectData {
  workspaceId: string;
}

/**
 * Registers a user
 *
 * @param userData - user data to be sent to the server
 * @returns A Promise that resolves to a success message.
 */
export const registerUser = (userData: IUserRegistrationData): Promise<any> => {
  return api.post(`/users`, { ...userData });
};

/**
 * Project List
 *
 * @param workspaceId - workspaceId
 * @returns A Promise that resolves to a success message.
 */
export const getProjectsList = (workspaceId: string): Promise<IProject[]> => {
  return api.get(`/workspaces/${workspaceId}/projects`, { withAuth: true });
};

/**
 * Project List
 *
 * @param workspaceId - workspaceId
 * @returns A Promise that resolves to a success message.
 */
export const getProject = ({ workspaceId, projectId }: IApiExtendedParams): Promise<IProject> => {
  return api.get(`/workspaces/${workspaceId}/projects/${projectId}`, {
    withAuth: true,
  });
};

/**
 * Create a Project
 *
 * @param workspaceId - workspaceId
 * @returns A Promise that resolves to a success message.
 */
export const createProject = ({ workspaceId, title, services }: ICreateProjectMutationProps): Promise<IProject> => {
  return api.post(`/workspaces/${workspaceId}/projects`, { title: title, services }, { withAuth: true });
};

/**
 * Delete a specific Project
 *
 * @param {object} params - The parameters for the DELETE request.
 * @param {string} params.workspaceId - The ID of the workspace to which the project belongs.
 * @param {string} params.projectId - The ID of the project to update.
 * @returns A Promise that resolves to a success message.
 */
export const deleteProject = ({ workspaceId, projectId }: IApiExtendedParams): Promise<IProject> => {
  return api.delete(`/workspaces/${workspaceId}/projects/${projectId}`, {
    withAuth: true,
  });
};

/**
 * Updates a project by making a PATCH request to the server.
 *
 * @param {object} params - The parameters for the PATCH request.
 * @param {string} params.workspaceId - The ID of the workspace to which the project belongs.
 * @param {string} params.projectId - The ID of the project to update.
 * @para {Object} params.projectData
 * @returns {Promise<IProject>} A Promise that resolves to the updated project data.
 */
export const patchProject = ({
  workspaceId,
  projectId,
  projectData,
}: {
  workspaceId: string;
  projectId: string;
  projectData: Record<string, any>;
}) => {
  return api.patch(
    `/workspaces/${workspaceId}/projects/${projectId}`,
    { ...projectData },
    {
      withAuth: true,
    }
  );
};

/**
 * Fetch the analytics for a specific project
 *
 * @param {object} params - The parameters for the POST request.
 * @param {string} params.workspaceId - The ID of the workspace to which the project belongs to.
 * @param {string} params.projectId - The ID of the project to fetch analytics for.
 * @returns A Promise that resolves to a success message.
 */
export const getAnalytics = ({ workspaceId, projectId, queryParams }: IAnalyticsAPIParams): Promise<IAnalytics[]> => {
  return api.get(`/workspaces/${workspaceId}/projects/${projectId}/analytics`, { queryParams: {...queryParams}, withAuth: true });
};
